import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  useInterviewBookingInfo,
  useJobMatchingSkills,
} from '@xing-com/crate-jobs-hooks';
import { useFeatureSwitches } from '@xing-com/hub';

type Props = { slug: string; jobId: string };

type InterviewBookingResponse = {
  isLoading: boolean;
  hasInterviewButtonAvailable: boolean;
  /**
   * `hasBookedInterview` validates both if the booking interview is enabled
   * and if there's a booking interview URL, for the given `jobId`.
   */
  hasBookedInterview: boolean;
  editInterviewUrl?: string;
};

export const useInterviewBooking = ({
  slug,
  jobId,
}: Props): InterviewBookingResponse => {
  const { isLoggedIn } = useLoginState();

  const { data, loading: loadingFeatureSwitches } = useFeatureSwitches();
  const isBookingInterviewEnabled =
    !loadingFeatureSwitches &&
    !!data?.includes('enable_interview_scheduling_from_job_ad');
  const isSkipMatchingSkillsEnabled =
    !loadingFeatureSwitches &&
    !!data?.includes('enable_skip_matching_skills_for_interview_scheduling');

  const { loading: isMatchingSkillsLoading, matchedSkills } =
    useJobMatchingSkills({ slug, skip: !jobId || !isLoggedIn });

  const hasUserMatchingSkills =
    (!!matchedSkills && matchedSkills.length > 3) ||
    isSkipMatchingSkillsEnabled;

  const {
    isLoading: isInterviewBookingInfoLoading,
    hasInterviewBookingAvailable,
    editInterviewBookingUrl,
  } = useInterviewBookingInfo({
    jobId,
    skip:
      !jobId ||
      !isLoggedIn ||
      !isBookingInterviewEnabled ||
      !hasUserMatchingSkills,
  });

  return {
    isLoading: isMatchingSkillsLoading || isInterviewBookingInfoLoading,
    hasInterviewButtonAvailable:
      isBookingInterviewEnabled &&
      hasUserMatchingSkills &&
      !!hasInterviewBookingAvailable &&
      !editInterviewBookingUrl,
    editInterviewUrl: editInterviewBookingUrl ?? undefined,
    hasBookedInterview: isBookingInterviewEnabled && !!editInterviewBookingUrl,
  };
};
